// Polyfill Node's Buffer for the browser — @react-pdf/renderer's image decoder
// (used by the Participation Report PDF) references Buffer, which Vite does not
// provide in the browser. Must run before any react-pdf code executes.
import { Buffer as BufferPolyfill } from 'buffer';
if (typeof globalThis.Buffer === 'undefined') {
  globalThis.Buffer = BufferPolyfill;
}

import React from 'react';
import ReactDOM from 'react-dom/client';
import { captureException, startSentry } from '@/lib/sentry-lazy';
import { registerSW } from 'virtual:pwa-register';
import App from './App';
import './index.css';
import { canReloadForNewBuild } from '@/utils/chunk-retry';

// Register the PWA service worker app-wide WITH an error handler. vite-plugin-pwa's
// auto-injected registration had no .catch(), so a transient iOS-Safari sw.js load
// failure bubbled up as an unhandled rejection (Sentry MAGNETFLOW-PORTAL-28). Here the
// failure is caught and logged instead. `injectRegister: null` in vite.config.ts turns
// off the uncaught auto-injection so this is the single source of registration.
// A deploy replaces every content-hashed asset and deletes the old ones, so a
// long-lived tab keeps running the previous entry bundle and 404s on the next
// lazily-loaded route — which is why idle logout, whose redirect to /auth is
// usually the first lazy route a user needs, landed on "Page Refresh Required".
//
// The service worker is the right detector: it compares sw.js itself, and
// activating the update REFRESHES THE PRECACHE, so the reload that follows
// genuinely serves the new index.html. Parsing HTML for the entry filename
// would instead race the precache and could reload into the same stale build.
//
// Two things are wired here:
//   1. a periodic update() — nothing else asks, so a tab open for hours never
//      notices a deploy;
//   2. onNeedReload — WITHOUT it, vite-plugin-pwa's autoUpdate mode calls
//      window.location.reload() the instant the new worker activates, with no
//      regard for what the user is doing. Supplying it makes this the single
//      place that decides when reloading is acceptable.
const SW_UPDATE_INTERVAL_MS = 15 * 60 * 1000;
let reloadPending = false;

registerSW({
  immediate: true,
  onRegisteredSW(_swScriptUrl, registration) {
    if (!registration) return;
    setInterval(() => {
      // Don't poll a tab that is offline or backgrounded into a frozen state.
      if (navigator.onLine === false) return;
      void registration.update().catch(() => { /* transient; try again later */ });
    }, SW_UPDATE_INTERVAL_MS);
  },
  onNeedReload() {
    // Two deploys in one long session call this twice; without the guard each
    // call attaches its own visibilitychange listener and both fire.
    if (reloadPending) return;
    reloadPending = true;

    // Deliberately NOT immediate. A reload discards in-progress form state, so
    // wait until the tab is hidden AND nothing on the page is holding unsaved
    // work. Hidden alone is not enough: someone filling in an application
    // switches away to look up a date of birth, and on a phone locking the
    // screen hides the page — reloading then hands them back an empty form.
    // Staying armed costs nothing; the next time the tab hides, the work is
    // usually saved. The chunk-error self-heal in chunk-retry.ts remains the
    // safety net if the user navigates into a missing chunk first.
    if (canReloadForNewBuild()) {
      window.location.reload();
      return;
    }
    const onHidden = () => {
      if (!canReloadForNewBuild()) return;
      document.removeEventListener('visibilitychange', onHidden);
      window.location.reload();
    };
    document.addEventListener('visibilitychange', onHidden);
  },
  onRegisterError(error) {
    console.warn('PWA: service worker registration failed', error);
  },
});

// Equivalent of Sentry.withErrorBoundary: report the error and show the same fallback.
// Reports through sentry-lazy so the SDK stays out of the entry bundle.
class AppErrorBoundary extends React.Component<{ children: React.ReactNode }, { error: unknown }> {
  state = { error: null as unknown };
  static getDerivedStateFromError(error: unknown) {
    return { error };
  }
  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    captureException(error, { contexts: { react: { componentStack: info.componentStack } } });
  }
  render() {
    const { error } = this.state;
    if (error === null) return this.props.children;
    return (
      <div style={{ padding: '2rem', fontFamily: 'system-ui' }}>
        <h2>Something went wrong</h2>
        <p>{error instanceof Error ? error.message : String(error)}</p>
        <button onClick={() => this.setState({ error: null })}>Try again</button>
      </div>
    );
  }
}

startSentry();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>
  </React.StrictMode>
);
